import { ApiError } from '@/lib/api'
/**
 * Клиент WebSocket-шлюза (спецификация 6.5): HELLO → IDENTIFY → READY,
 * heartbeat по интервалу сервера, переподключение с задержкой.
 */
export type GatewayStatus = 'idle' | 'connecting' | 'identifying' | 'ready' | 'reconnecting' | 'closed'

interface Payload {
  op: number
  d?: unknown
  s?: number | null
  t?: string | null
}

const Op = {
  Dispatch: 0,
  Heartbeat: 1,
  Identify: 2,
  PresenceUpdate: 3,
  Resume: 6,
  Reconnect: 7,
  InvalidSession: 9,
  Hello: 10,
  HeartbeatAck: 11,
  SubscribeMemberList: 14,
} as const

/** Коды закрытия, после которых переподключаться бессмысленно. */
const FATAL_CLOSE = new Set([4004, 4010, 4011, 4012, 4013, 4014])

/*
 * Мёртвое соединение. Ноутбук уснул — сервер через полтора интервала без heartbeat закрыл сессию,
 * а браузер об этом не узнал: сокет «открыт», событий нет, пока не обновишь страницу. То же после
 * смены сети или переподключения VPN. Поэтому сверяем часы (сон — скачок времени), ждём ответа
 * на каждый heartbeat и проверяем соединение, когда вернулась сеть или окно снова на экране.
 */
/** Ответа на heartbeat нет столько — соединение мёртвое. */
const ACK_TIMEOUT = 15_000
/** HELLO сервер шлёт сразу после рукопожатия; нет его столько — подключение повисло. */
const HELLO_TIMEOUT = 15_000
/** Как часто сверяем часы. */
const CLOCK_TICK = 2_000
/** Скачок часов больше этого — компьютер спал. Фоновой вкладке Chrome будит таймеры раз в минуту. */
const CLOCK_JUMP = 15_000
const CLOCK_JUMP_HIDDEN = 90_000
/** Внеочередной heartbeat, когда окно снова на экране, — не чаще этого. */
const PROBE_GAP = 10_000

type Listener = (data: unknown) => void

export class GatewayClient {
  status: GatewayStatus = 'idle'
  onStatus: ((status: GatewayStatus) => void) | null = null

  private ws: WebSocket | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private firstBeatTimer: ReturnType<typeof setTimeout> | null = null
  private ackTimer: ReturnType<typeof setTimeout> | null = null
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null
  private memberSubscription: { guild_id: string; channels: Record<string, [number, number][]> } | null = null
  private helloTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private clockTimer: ReturnType<typeof setInterval> | null = null
  private interval = 0
  /** Когда ушёл последний heartbeat (или пришёл HELLO); по часам, а не по таймерам. */
  private lastBeat = 0
  private lastTick = 0
  private seq: number | null = null
  private sessionId: string | null = null
  private attempts = 0
  /** Сервер сам попросил переподключиться (выкладка): возвращаться сразу, без паузы. */
  private reconnectSoon = false
  private closedByUser = false
  private readonly listeners = new Map<string, Set<Listener>>()
  /*
   * Что сообщаем о себе в op 3: игра (оболочка) и «отошёл» (10 минут без ввода). Каждый op 3
   * у Discord заменяет присутствие целиком, поэтому шлём оба поля всегда — иначе минутное
   * напоминание об игре снимало бы «отошёл», а «отошёл» стирал бы игру. Сам статус (в сети,
   * не беспокоить, невидимый) выбирается в настройках, op 3 его не меняет.
   */
  private game: { name: string; type: number; steam_app_id?: string }[] = []
  private idleSince: number | null = null

  constructor(
    private readonly url: string,
    /**
     * Возвращает действующий токен: строка или функция с обновлением по истечении.
     * force — обновить принудительно: шлюз отверг токен, который мы считали годным.
     */
    private readonly token: string | ((force?: boolean) => Promise<string>),
  ) {}

  /*
   * Токен для шлюза. Ошибку обновления важно различать: 401 на refresh — сессии больше нет,
   * и человек должен выйти; всё остальное — API временно недоступен (выкладка, сеть, 429) —
   * и правильно подождать и переподключиться. До 21.09.2026 любая ошибка считалась концом
   * сессии, и каждая выкладка разлогинивала тех, у кого в этот момент истекал access-токен.
   */
  private async resolveToken(force = false): Promise<{ token: string } | { gone: true } | { retry: true }> {
    if (typeof this.token === 'string') return { token: this.token }
    try {
      return { token: await this.token(force) }
    } catch (err) {
      return isSessionGone(err) ? { gone: true } : { retry: true }
    }
  }

  /** Шлюз закрыл соединение как неаутентифицированное: обновляем токен и решаем, выходить ли. */
  private async recoverAuth(ev: CloseEvent): Promise<void> {
    const r = await this.resolveToken(true)
    if (this.closedByUser || this.ws) return
    if ('gone' in r) {
      this.setStatus('closed')
      this.emit('close', { code: ev.code, reason: ev.reason })
      this.emit('auth_failed', null)
      return
    }
    this.scheduleReconnect()
  }

  connect(): void {
    this.closedByUser = false
    this.watch()
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.setStatus(this.attempts ? 'reconnecting' : 'connecting')
    const ws = new WebSocket(`${this.url}/?v=1&encoding=json`)
    this.ws = ws
    // сеть после сна ещё не поднялась, рукопожатие висит — не ждём, пока сдастся браузер
    this.helloTimer = setTimeout(() => this.restart('hello timeout', false), HELLO_TIMEOUT)
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return
      let p: Payload
      try {
        p = JSON.parse(String(ev.data)) as Payload
      } catch {
        return
      }
      this.handle(p)
    }
    ws.onclose = (ev) => {
      if (this.ws !== ws) return
      if (ev.code === 4007 || ev.code === 1000) this.clearResume()
      this.stopTimers()
      this.ws = null
      if (ev.code === 1001 || ev.code === 1012) this.reconnectSoon = true
      if (ev.code === 4004 && !this.closedByUser) {
        // сервер не принял токен: возможно, он просто истёк, пока API был недоступен
        void this.recoverAuth(ev)
        return
      }
      if (this.closedByUser || FATAL_CLOSE.has(ev.code)) {
        this.setStatus('closed')
        this.emit('close', { code: ev.code, reason: ev.reason })
        return
      }
      this.scheduleReconnect()
    }
    ws.onerror = () => {
      /* onclose придёт следом */
    }
  }

  close(): void {
    this.closedByUser = true
    this.clearResume()
    this.memberSubscription = null
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.stopTimers()
    this.unwatch()
    this.ws?.close(1000, 'client closed')
    this.ws = null
    this.setStatus('closed')
  }

  /** Идентификатор сессии из READY — им подписывается голосовое состояние. */
  get session(): string | null {
    return this.sessionId
  }

  /** Во что играем (op 3). null убирает статус. */
  setActivity(game: { name: string; steamAppId?: string } | null): void {
    this.game = game ? [{ name: game.name, type: 0, ...(game.steamAppId ? { steam_app_id: game.steamAppId } : {}) }] : []
    this.send(Op.PresenceUpdate, this.presence())
  }

  /** Отошёл ли человек (op 3): сервер делает «неактивен», когда отошли все его сессии. */
  setIdle(idle: boolean): void {
    if (idle === (this.idleSince !== null)) return
    this.idleSince = idle ? Date.now() : null
    this.send(Op.PresenceUpdate, this.presence())
  }

  /** Запущена ли игра: пока играет, человек за компьютером, даже если окно Кличата не трогает. */
  get playing(): boolean {
    return this.game.length > 0
  }

  private presence() {
    const idle = this.idleSince !== null
    return { status: idle ? 'idle' : 'online', since: this.idleSince ?? 0, afk: idle, activities: this.game }
  }

  /** Ленивый список участников (op 14): диапазоны строк по 100 для одного канала сервера. */
  subscribeMemberList(guildId: string, channelId: string, ranges: [number, number][]): void {
    this.memberSubscription = { guild_id: guildId, channels: { [channelId]: ranges } }
    this.send(Op.SubscribeMemberList, this.memberSubscription)
  }

  on(event: string, fn: Listener): () => void {
    let set = this.listeners.get(event)
    if (!set) {
      set = new Set()
      this.listeners.set(event, set)
    }
    set.add(fn)
    return () => set?.delete(fn)
  }

  private handle(p: Payload): void {
    switch (p.op) {
      case Op.Hello: {
        if (this.helloTimer) clearTimeout(this.helloTimer)
        this.helloTimer = null
        const interval = (p.d as { heartbeat_interval: number }).heartbeat_interval
        this.startHeartbeat(interval)
        const ws = this.ws
        void this.resolveToken().then((r) => {
          if (this.ws !== ws) return // пока ждали токен, соединение сменилось
          if ('gone' in r) {
            this.setStatus('closed')
            this.emit('auth_failed', null)
            this.ws?.close(4004, 'no token')
            return
          }
          if ('retry' in r) {
            // API не ответил — не выходим, а переподключаемся позже (onclose запланирует)
            this.ws?.close(4000, 'token unavailable')
            return
          }
          const { token } = r
          this.startHandshakeTimer()
          if (this.sessionId && this.seq !== null) {
            this.send(Op.Resume, { token, session_id: this.sessionId, seq: this.seq })
          } else {
            this.identify(token)
          }
        })
        break
      }
      case Op.HeartbeatAck:
        if (this.ackTimer) clearTimeout(this.ackTimer)
        this.ackTimer = null
        break
      case Op.Heartbeat:
        this.send(Op.Heartbeat, this.seq)
        break
      case Op.Dispatch: {
        // READY starts a new sequence; replay must continue the existing one.
        if (p.t !== 'READY' && typeof p.s === 'number' && this.seq !== null) {
          if (p.s <= this.seq) return // duplicate replay packet
          if (!Number.isSafeInteger(p.s) || p.s !== this.seq + 1) {
            this.clearResume()
            this.restart('sequence gap')
            return
          }
        }
        if (typeof p.s === 'number') this.seq = p.s
        if (p.t === 'READY') {
          this.sessionId = (p.d as { session_id: string }).session_id
          this.finishHandshake()
          if (this.memberSubscription) this.send(Op.SubscribeMemberList, this.memberSubscription)
        }
        if (p.t === 'RESUMED') {
          this.finishHandshake()
          // Activity/idle can change while disconnected. Restore the current
          // values, and refresh the lazy member-list subscription on this socket.
          this.send(Op.PresenceUpdate, this.presence())
          if (this.memberSubscription) this.send(Op.SubscribeMemberList, this.memberSubscription)
        }
        if (p.t) this.emit(p.t, p.d)
        break
      }
      case Op.InvalidSession: {
        if (p.d !== true) this.clearResume()
        const ws = this.ws
        setTimeout(() => {
          if (this.ws !== ws) return
          void this.resolveToken().then((r) => {
            if (this.ws !== ws) return
            if ('token' in r) {
              this.startHandshakeTimer()
              if (this.sessionId && this.seq !== null) this.send(Op.Resume, { token: r.token, session_id: this.sessionId, seq: this.seq })
              else this.identify(r.token)
            }
            else this.ws?.close('gone' in r ? 4004 : 4000, 'token unavailable')
          })
        }, 1000 + Math.random() * 4000)
        break
      }
      case Op.Reconnect:
        // сервер уходит на перезапуск, рядом уже новый: возвращаемся сразу, а не через 2–3 секунды
        this.reconnectSoon = true
        this.ws?.close(4000, 'reconnect requested')
        break
      default:
        break
    }
  }

  private clearResume(): void {
    this.sessionId = null
    this.seq = null
  }

  private startHandshakeTimer(): void {
    if (this.handshakeTimer) clearTimeout(this.handshakeTimer)
    this.handshakeTimer = setTimeout(() => this.restart('handshake timeout'), 30_000)
  }

  private finishHandshake(): void {
    if (this.handshakeTimer) clearTimeout(this.handshakeTimer)
    this.handshakeTimer = null
    this.attempts = 0
    this.setStatus('ready')
  }

  private identify(token: string): void {
    this.setStatus('identifying')
    this.send(Op.Identify, {
      token,
      properties: { os: navigator.platform, browser: 'klichat-web', device: 'klichat-web' },
      // после переподключения сессия сразу приходит «отошедшей», если человек так и не вернулся
      presence: this.presence(),
    })
  }

  private send(op: number, d: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ op, d }))
  }

  private startHeartbeat(interval: number): void {
    this.stopTimers()
    this.interval = interval
    this.lastBeat = Date.now()
    // первый heartbeat — через случайную долю интервала, как рекомендует Discord
    this.firstBeatTimer = setTimeout(() => this.beat(), Math.floor(interval * Math.random()))
    this.heartbeatTimer = setInterval(() => this.beat(), interval)
  }

  private beat(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    // heartbeat не уходил дольше, чем сервер держит сессию без него, — компьютер спал, сессии уже нет
    if (this.status === 'ready' && Date.now() - this.lastBeat > this.interval * 1.5) {
      this.restart('asleep')
      return
    }
    this.lastBeat = Date.now()
    this.send(Op.Heartbeat, this.seq)
    // пока собирается READY, сервер не читает следующие пакеты — ответа ждём только после него
    if (this.status === 'ready') this.ackTimer ??= setTimeout(() => this.restart('heartbeat timeout'), ACK_TIMEOUT)
  }

  private stopTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    for (const t of [this.firstBeatTimer, this.ackTimer, this.helloTimer, this.handshakeTimer]) if (t) clearTimeout(t)
    this.heartbeatTimer = this.firstBeatTimer = this.ackTimer = this.helloTimer = this.handshakeTimer = null
  }

  /**
   * Бросить соединение и сразу подключиться заново. Закрытия не ждём: мёртвый сокет закрывается
   * минутами, браузер ждёт ответа сервера, которого не будет. Сохраняем session_id и seq: сервер решит,
   * можно ли ещё дослать события или нужен новый READY. soon = false — обычная растущая пауза (подключение повисло).
   */
  private restart(reason: string, soon = true): void {
    const ws = this.ws
    if (!ws) return
    ws.onmessage = null
    ws.onclose = null
    ws.onerror = null
    ws.close(4000, reason)
    this.ws = null
    this.stopTimers()
    this.reconnectSoon = soon
    this.scheduleReconnect()
  }

  /** Сверка часов, возврат сети и окна — пока клиент не закрыт. */
  private watch(): void {
    if (this.clockTimer || typeof window === 'undefined') return
    this.lastTick = Date.now()
    this.clockTimer = setInterval(this.onTick, CLOCK_TICK)
    window.addEventListener('online', this.onOnline)
    window.addEventListener('focus', this.onLook)
    document.addEventListener('visibilitychange', this.onLook)
  }

  private unwatch(): void {
    if (!this.clockTimer) return
    clearInterval(this.clockTimer)
    this.clockTimer = null
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('focus', this.onLook)
    document.removeEventListener('visibilitychange', this.onLook)
  }

  /** Таймеры спящего компьютера стоят, а часы идут: большой скачок между тиками — проснулись. */
  private readonly onTick = () => {
    const now = Date.now()
    const slept = now - this.lastTick > (document.hidden ? CLOCK_JUMP_HIDDEN : CLOCK_JUMP)
    this.lastTick = now
    if (slept) this.wake()
  }

  private readonly onOnline = () => this.wake()

  private readonly onLook = () => {
    if (document.visibilityState === 'visible') this.probe()
  }

  /** Проснулись или вернулась сеть: переподключение не ждёт паузы, открытое соединение проверяем. */
  private wake(): void {
    if (this.closedByUser) return
    if (this.reconnectTimer) {
      this.connect()
      return
    }
    this.probe()
  }

  /** Heartbeat вне очереди: нет ответа за ACK_TIMEOUT — переподключаемся. */
  private probe(): void {
    if (this.status !== 'ready' || this.ackTimer) return
    if (Date.now() - this.lastBeat > PROBE_GAP) this.beat()
  }

  private scheduleReconnect(): void {
    /*
     * Обычный обрыв — растущая пауза, чтобы не долбить лежащий сервер. Просьба сервера
     * (op 7 или «ухожу», 1001/1012) — полсекунды с разбросом: новый процесс уже принимает,
     * а разброс не даёт всем клиентам прийти в одну миллисекунду.
     */
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.reconnectSoon) {
      this.reconnectSoon = false
      this.attempts = 0
      this.setStatus('reconnecting')
      this.reconnectTimer = setTimeout(() => this.connect(), 200 + Math.random() * 600)
      return
    }
    this.attempts += 1
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(this.attempts, 5)) + Math.random() * 1000
    this.setStatus('reconnecting')
    this.reconnectTimer = setTimeout(() => this.connect(), delay)
  }

  private setStatus(status: GatewayStatus): void {
    this.status = status
    this.onStatus?.(status)
  }

  private emit(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach((fn) => fn(data))
    // подписка на всё: '*' получает {t, d} для каждого DISPATCH
    if (event !== 'close' && event !== 'auth_failed') this.listeners.get('*')?.forEach((fn) => fn({ t: event, d: data }))
  }
}

/**
 * Сессии больше нет — только когда сервер прямо сказал 401 на обновление токена
 * (или refresh-токена нет вовсе). Сеть, 5xx и 429 — временно, сессия жива.
 */
export function isSessionGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401
}
